import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Chip,
  Grid,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clientAssignmentService, ClientAssignment } from '../../services/clientAssignmentService';
import { clientService } from '../../services/clientService';
import { userService } from '../../services/userService';

interface ClientAssignmentManagerProps {
  open: boolean;
  onClose: () => void;
}

const ClientAssignmentManager: React.FC<ClientAssignmentManagerProps> = ({
  open,
  onClose,
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedAdminId, setSelectedAdminId] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<ClientAssignment | null>(null);

  const queryClient = useQueryClient();

  // Fetch all assignments
  const { data: assignmentsData, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['client-assignments'],
    queryFn: () => clientAssignmentService.getAllAssignments(),
    enabled: open,
  });

  // Fetch clients for assignment dropdown
  const { data: clientsData } = useQuery({
    queryKey: ['clients-for-assignment'],
    queryFn: () => clientService.getClients({ page: 0, limit: 100 }),
    enabled: assignDialogOpen,
  });

  // Fetch admin users for assignment dropdown
  const { data: usersData } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => userService.getUsers({ page: 1, limit: 100, includeInactive: false }),
    enabled: assignDialogOpen,
  });

  // Create assignment mutation
  const createAssignmentMutation = useMutation({
    mutationFn: clientAssignmentService.createAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setAssignDialogOpen(false);
      setSelectedClientId('');
      setSelectedAdminId('');
    },
  });

  // Remove assignment mutation
  const removeAssignmentMutation = useMutation({
    mutationFn: clientAssignmentService.removeAssignment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      setDeleteDialogOpen(false);
      setAssignmentToDelete(null);
    },
  });

  const assignments = assignmentsData?.data || [];
  const clients = clientsData?.data || [];
  const adminUsers = usersData?.data?.filter(user => user.role === 'ADMIN') || [];

  const handleAssignClient = () => {
    if (selectedClientId && selectedAdminId) {
      createAssignmentMutation.mutate({
        clientId: selectedClientId,
        adminId: selectedAdminId,
      });
    }
  };

  const handleRemoveAssignment = () => {
    if (assignmentToDelete) {
      removeAssignmentMutation.mutate(assignmentToDelete.id);
    }
  };

  const getUnassignedClients = () => {
    const assignedClientIds = assignments.map(a => a.clientId);
    return clients.filter(client => !assignedClientIds.includes(client.id));
  };

  const getAvailableAdmins = () => {
    if (!selectedClientId) return adminUsers;
    
    const currentAssignments = assignments.filter(a => a.clientId === selectedClientId);
    const assignedAdminIds = currentAssignments.map(a => a.adminId);
    return adminUsers.filter(admin => !assignedAdminIds.includes(admin.id));
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            maxWidth: '1100px'
          }
        }}
      >
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '22px 26px 18px',
          borderBottom: '1px solid var(--border-light)'
        }}>
          <Box>
            <DialogTitle sx={{ padding: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>
              Client Assignment Management
            </DialogTitle>
            <Box sx={{ fontSize: '12.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
              Manage client assignments to admin users
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px' }}>
                <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="8.5" cy="7" r="4"/>
                <line x1="20" y1="8" x2="20" y2="14"/>
                <line x1="23" y1="11" x2="17" y2="11"/>
              </svg>
            }
            onClick={() => setAssignDialogOpen(true)}
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: 'var(--accent)',
              color: 'white',
              boxShadow: '0 1px 2px rgba(37,99,235,0.2)',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
                boxShadow: 'var(--shadow-md)'
              }
            }}
          >
            Assign Client
          </Button>
        </Box>
        <DialogContent sx={{ padding: '22px 26px' }}>
          {assignmentsLoading ? (
            <Box sx={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)', fontSize: '13px' }}>
              Loading assignments...
            </Box>
          ) : (
            <TableContainer sx={{ 
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              boxShadow: 'var(--shadow)',
              overflowX: 'auto'
            }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Client</TableCell>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Company</TableCell>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Assigned Admin</TableCell>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Assigned By</TableCell>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Assigned Date</TableCell>
                    <TableCell sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      padding: '12px 18px',
                      borderBottom: '1px solid var(--border-light)',
                      background: 'var(--bg)',
                      whiteSpace: 'nowrap'
                    }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ padding: '40px 18px', fontSize: '13px', color: 'var(--text-muted)', borderBottom: '1px solid var(--border-light)' }}>
                        No client assignments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    assignments.map((assignment) => (
                      <TableRow 
                        key={assignment.id}
                        sx={{
                          '&:hover': {
                            background: '#fafbff'
                          }
                        }}
                      >
                        <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                          <Box>
                            <Typography sx={{ fontWeight: 500, color: 'var(--text-primary)', fontSize: '13px' }}>
                              {assignment.client.name}
                            </Typography>
                            <Typography sx={{ fontSize: '11.5px', color: 'var(--text-muted)', marginTop: '2px' }}>
                              {assignment.client.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ padding: '13px 18px', fontSize: '12.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)' }}>
                          {assignment.client.company || 'N/A'}
                        </TableCell>
                        <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                          <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '5px',
                            padding: '4px 10px',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: 500,
                            background: 'var(--accent-soft)',
                            color: 'var(--accent)',
                            border: '1px solid rgba(37,99,235,0.2)'
                          }}>
                            {clientAssignmentService.formatAdminName(assignment.admin)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ padding: '13px 18px', fontSize: '12.5px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)' }}>
                          {clientAssignmentService.formatAdminName(assignment.assignedBy)}
                        </TableCell>
                        <TableCell sx={{ padding: '13px 18px', fontSize: '12px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle', color: 'var(--text-muted)', fontFamily: 'DM Mono, monospace' }}>
                          {clientAssignmentService.formatAssignmentDate(assignment.createdAt)}
                        </TableCell>
                        <TableCell sx={{ padding: '13px 18px', fontSize: '13px', borderBottom: '1px solid var(--border-light)', verticalAlign: 'middle' }}>
                          <IconButton
                            size="small"
                            onClick={() => {
                              setAssignmentToDelete(assignment);
                              setDeleteDialogOpen(true);
                            }}
                            sx={{
                              width: '30px',
                              height: '30px',
                              borderRadius: '6px',
                              border: 'none',
                              background: 'none',
                              transition: 'all 0.15s',
                              '&:hover': {
                                background: 'var(--red-soft)',
                                color: 'var(--red)',
                                '& svg': {
                                  color: 'var(--red)'
                                }
                              }
                            }}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '14px', height: '14px', color: 'var(--text-muted)' }}>
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '16px 26px',
          borderTop: '1px solid var(--border-light)'
        }}>
          <Button 
            onClick={onClose}
            sx={{
              padding: '8px 16px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              border: '1px solid var(--border)',
              color: 'var(--text-secondary)',
              backgroundColor: 'var(--surface)',
              '&:hover': {
                backgroundColor: 'var(--bg)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border)'
              }
            }}
          >
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Assign Client Dialog */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: 'var(--shadow-lg)',
            border: '1px solid var(--border)',
            maxWidth: '520px'
          }
        }}
      >
        <Box sx={{ padding: '24px 28px 20px' }}>
          <Typography sx={{ fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '-0.3px' }}>
            Assign Client to Admin
          </Typography>
        </Box>
        <DialogContent sx={{ padding: '0 28px 24px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            <Box>
              <Typography sx={{
                fontSize: '13px',
                fontWeight: 500,
                color: 'var(--accent)',
                marginBottom: '8px',
                letterSpacing: '-0.1px'
              }}>
                Select Client
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  displayEmpty
                  sx={{
                    fontSize: '14px',
                    color: selectedClientId ? 'var(--text-primary)' : 'var(--text-muted)',
                    backgroundColor: 'white',
                    border: '1.5px solid var(--accent)',
                    borderRadius: 'var(--radius-sm)',
                    height: '44px',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                      paddingRight: '32px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.1)'
                    }
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    Select Client
                  </MenuItem>
                  {getUnassignedClients().map((client) => (
                    <MenuItem key={client.id} value={client.id} sx={{ fontSize: '14px' }}>
                      {client.name} ({client.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box>
              <Typography sx={{
                fontSize: '14px',
                fontWeight: 400,
                color: 'var(--text-secondary)',
                marginBottom: '8px',
                letterSpacing: '-0.1px'
              }}>
                Select Admin
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={selectedAdminId}
                  onChange={(e) => setSelectedAdminId(e.target.value)}
                  disabled={!selectedClientId}
                  displayEmpty
                  sx={{
                    fontSize: '14px',
                    color: selectedAdminId ? 'var(--text-primary)' : 'var(--text-muted)',
                    backgroundColor: 'white',
                    border: '1px solid var(--border)',
                    borderRadius: 'var(--radius-sm)',
                    height: '44px',
                    '& .MuiSelect-select': {
                      padding: '10px 14px',
                      paddingRight: '32px'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      border: 'none'
                    },
                    '&.Mui-focused': {
                      border: '1.5px solid var(--accent)',
                      boxShadow: '0 0 0 3px rgba(37,99,235,0.1)'
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'var(--bg)',
                      opacity: 0.6
                    }
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                    Select Admin
                  </MenuItem>
                  {getAvailableAdmins().map((admin) => (
                    <MenuItem key={admin.id} value={admin.id} sx={{ fontSize: '14px' }}>
                      {admin.firstName} {admin.lastName} ({admin.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
          
          {createAssignmentMutation.error && (
            <Alert 
              severity="error" 
              sx={{ 
                mt: 2,
                borderRadius: 'var(--radius-sm)',
                fontSize: '12.5px',
                '& .MuiAlert-icon': {
                  fontSize: '18px'
                }
              }}
            >
              {(createAssignmentMutation.error as any)?.response?.data?.error || 'Failed to assign client'}
            </Alert>
          )}
        </DialogContent>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '10px',
          padding: '16px 28px 24px'
        }}>
          <Button 
            onClick={() => setAssignDialogOpen(false)}
            sx={{
              padding: '8px 18px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              color: 'var(--accent)',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(37,99,235,0.05)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAssignClient}
            variant="contained"
            disabled={!selectedClientId || !selectedAdminId || createAssignmentMutation.isPending}
            sx={{
              padding: '8px 18px',
              borderRadius: 'var(--radius-sm)',
              fontSize: '13px',
              fontWeight: 500,
              textTransform: 'none',
              backgroundColor: !selectedClientId || !selectedAdminId ? 'var(--border)' : 'var(--accent)',
              color: 'white',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: 'var(--accent-hover)',
                boxShadow: 'var(--shadow-md)'
              },
              '&:disabled': {
                backgroundColor: 'var(--border)',
                color: 'var(--text-muted)',
                opacity: 0.6
              }
            }}
          >
            {createAssignmentMutation.isPending ? 'Assigning...' : 'Assign Client'}
          </Button>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Remove Client Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove the assignment of "{assignmentToDelete?.client.name}" 
            from "{assignmentToDelete ? clientAssignmentService.formatAdminName(assignmentToDelete.admin) : ''}"?
          </Typography>
          
          {removeAssignmentMutation.error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {(removeAssignmentMutation.error as any)?.response?.data?.error || 'Failed to remove assignment'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleRemoveAssignment}
            color="error"
            variant="contained"
            disabled={removeAssignmentMutation.isPending}
          >
            {removeAssignmentMutation.isPending ? 'Removing...' : 'Remove Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientAssignmentManager;
